import {
  DEFAULT_API_BASE_URL,
  DEFAULT_BOOKING_BASE_URL,
  DEFAULT_BOOKING_FLOW,
  DEFAULT_MODE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGINATION,
} from './constants';
import type {
  BookingFlow,
  HostMode,
  OrderBy,
  PaginationMode,
  ResolvedSearchParams,
  WidgetConfig,
  WidgetTheme,
} from './types';

export interface NormalizedConfig
  extends Omit<
    WidgetConfig,
    | 'mode'
    | 'showFilter'
    | 'pagination'
    | 'pageSize'
    | 'bookingUrlMode'
    | 'bookingFlow'
    | 'allowAnyCity'
    | 'orderBy'
    | 'theme'
    | 'columns'
    | 'guests'
    | 'children'
    | 'childrenAges'
  > {
  container: HTMLElement;
  mode: HostMode;
  showFilter: boolean;
  pagination: PaginationMode;
  pageSize: number;
  bookingFlow: BookingFlow;
  allowAnyCity: boolean;
  columns: number | null;
  orderBy: OrderBy | null;
  apiBaseUrl: string;
  bookingBaseUrl: string;
  theme: WidgetTheme;
  initialSearch: Partial<ResolvedSearchParams> & {
    cityName: string | null;
    childrenAges: number[] | null;
  };
}

const KEBAB_TO_CAMEL: Record<string, string> = {
  'customer-id': 'customerId',
  mode: 'mode',
  'show-filter': 'showFilter',
  pagination: 'pagination',
  'page-size': 'pageSize',
  'booking-url-mode': 'bookingUrlMode',
  'booking-flow': 'bookingFlow',
  'return-url': 'returnUrl',
  'order-by': 'orderBy',
  columns: 'columns',
  'allow-any-city': 'allowAnyCity',
  'theme-accent': 'themeAccent',
  'theme-radius': 'themeRadius',
  'theme-font': 'themeFont',
  'api-base-url': 'apiBaseUrl',
  'booking-base-url': 'bookingBaseUrl',
  city: 'city',
  'check-in': 'checkIn',
  'check-out': 'checkOut',
  guests: 'guests',
  children: 'children',
  'children-ages': 'childrenAges',
};

function coerce(key: string, raw: string): unknown {
  switch (key) {
    case 'showFilter':
    case 'allowAnyCity':
      return raw === 'true' || raw === '1' || raw === '';
    case 'pageSize':
    case 'columns':
    case 'themeRadius':
    case 'guests':
    case 'children':
      return Number.parseInt(raw, 10);
    case 'childrenAges': {
      // data-children-ages="3,12" → [3, 12]
      const ages = raw
        .split(',')
        .map((piece) => Number.parseInt(piece.trim(), 10))
        .filter((age) => Number.isFinite(age));
      return ages.length ? ages : undefined;
    }
    default:
      return raw;
  }
}

function readDataAttrs(el: HTMLElement): Partial<WidgetConfig> {
  const out: Record<string, unknown> = {};
  for (const attr of Array.from(el.attributes)) {
    if (!attr.name.startsWith('data-')) continue;
    const key = KEBAB_TO_CAMEL[attr.name.slice(5)];
    if (!key) continue;
    const value = coerce(key, attr.value);
    if (
      value === undefined ||
      (typeof value === 'number' && Number.isNaN(value))
    )
      continue;
    out[key] = value;
  }
  return out as Partial<WidgetConfig>;
}

function resolveContainer(
  input: string | HTMLElement | undefined,
): HTMLElement {
  if (input instanceof HTMLElement) return input;
  if (typeof input === 'string') {
    const found = document.querySelector(input);
    if (found instanceof HTMLElement) return found;
    throw new Error(`[RHApartmentWidget] container "${input}" not found`);
  }
  throw new Error('[RHApartmentWidget] container is required');
}

function validateDates(checkIn?: string, checkOut?: string): void {
  if (!checkIn && !checkOut) return;
  if (checkIn && checkOut && checkIn >= checkOut) {
    throw new Error('[RHApartmentWidget] checkOut must be later than checkIn');
  }
}

export function normalizeConfig(input: WidgetConfig): NormalizedConfig {
  const container = resolveContainer(input.container);
  const fromAttrs = readDataAttrs(container);
  // JS config wins; data-* fills gaps. Mirrors the precedence documented in the
  // widget README so embedders can rely on it.
  const merged: WidgetConfig = { ...fromAttrs, ...input } as WidgetConfig;

  if (!merged.customerId) {
    throw new Error('[RHApartmentWidget] customerId is required');
  }

  const guests = merged.guests ?? 2;
  const ages = normalizeChildrenAges(merged.childrenAges);
  // When the embedder passes explicit ages, the array length is the source of
  // truth for the children count — even if `children` was also provided.
  const children = ages ? ages.length : merged.children ?? 0;
  if (guests < 1) throw new Error('[RHApartmentWidget] guests must be >= 1');
  if (children < 0)
    throw new Error('[RHApartmentWidget] children must be >= 0');
  validateDates(merged.checkIn, merged.checkOut);

  return {
    ...merged,
    container,
    mode: (merged.mode as HostMode) ?? DEFAULT_MODE,
    showFilter: merged.showFilter ?? true,
    pagination: (merged.pagination as PaginationMode) ?? DEFAULT_PAGINATION,
    pageSize: merged.pageSize ?? DEFAULT_PAGE_SIZE,
    // bookingUrlMode — старое имя опции (redirect | new_tab); если задано
    // только оно, сохраняем прежнее поведение.
    bookingFlow:
      (merged.bookingFlow as BookingFlow) ??
      (merged.bookingUrlMode as BookingFlow | undefined) ??
      DEFAULT_BOOKING_FLOW,
    allowAnyCity:
      merged.allowAnyCity ?? merged.mode === 'client_plus_other_cities',
    columns: normalizeColumns(merged.columns),
    orderBy:
      merged.orderBy === 'price' || merged.orderBy === '-price'
        ? merged.orderBy
        : null,
    theme: mergeTheme(merged),
    apiBaseUrl: (merged.apiBaseUrl ?? DEFAULT_API_BASE_URL).replace(/\/$/, ''),
    bookingBaseUrl: (merged.bookingBaseUrl ?? DEFAULT_BOOKING_BASE_URL).replace(
      /\/$/,
      '',
    ),
    initialSearch: {
      cityName: merged.city ?? null,
      checkIn: merged.checkIn,
      checkOut: merged.checkOut,
      guests,
      children,
      childrenAges: ages,
    },
  };
}

export function normalizeChildrenAges(
  input: number[] | undefined,
): number[] | null {
  if (!input || !Array.isArray(input)) return null;
  const cleaned = input
    .map((value) => Math.trunc(Number(value)))
    .filter((value) => Number.isFinite(value));
  for (const age of cleaned) {
    if (age < 0 || age > 17) {
      throw new Error(
        '[RHApartmentWidget] each childrenAges entry must be 0..17',
      );
    }
  }
  return cleaned.length ? cleaned : null;
}

function normalizeColumns(value: number | undefined): number | null {
  if (value === undefined || !Number.isFinite(value)) return null;
  return Math.min(4, Math.max(1, Math.trunc(value)));
}

const COLOR_RE = /^#[0-9a-f]{3,8}$/i;

// data-theme-* атрибуты и объект theme сливаем в одну тему; невалидные цвета
// отбрасываем, чтобы в CSS custom property не попал произвольный текст.
function mergeTheme(merged: WidgetConfig): WidgetTheme {
  const attrs = merged as unknown as Record<string, unknown>;
  const theme: WidgetTheme = { ...(merged.theme ?? {}) };
  if (typeof attrs.themeAccent === 'string' && !theme.accent)
    theme.accent = attrs.themeAccent;
  if (typeof attrs.themeRadius === 'number' && theme.radius === undefined)
    theme.radius = attrs.themeRadius;
  if (typeof attrs.themeFont === 'string' && !theme.fontFamily)
    theme.fontFamily = attrs.themeFont;
  for (const key of ['accent', 'accentHover', 'accentText'] as const) {
    if (theme[key] && !COLOR_RE.test(theme[key] as string)) delete theme[key];
  }
  if (theme.fontFamily && /[;{}<>]/.test(theme.fontFamily))
    delete theme.fontFamily;
  if (theme.radius !== undefined && !(theme.radius >= 0 && theme.radius <= 40))
    delete theme.radius;
  return theme;
}
